/**
 * Application routing configuration
 * Defines all routes with automatic error boundaries
 */
import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ErrorBoundary } from '@components/common';
import { LoadingFallback } from '@components/common/LoadingFallback/LoadingFallback';
import { ProtectedRoute } from '@components/common/ProtectedRoute/ProtectedRoute';
import MainLayout from '@components/layout/MainLayout/MainLayout';
import AuthLayout from '@components/layout/AuthLayout/AuthLayout';
import { ROUTES } from '@core/constants/routes.constants';
import { wrapRoute } from './RouteWrapper';

// Lazy load pages
const Login = lazy(() => import('@features/auth/pages/Login'));
const Register = lazy(() => import('@features/auth/pages/Register'));
const Dashboard = lazy(() => import('@features/dashboard/pages/Dashboard'));
const Projects = lazy(() => import('@features/projects/pages/Projects'));
const ProjectEditPage = lazy(() => import('@features/projects/pages/ProjectEditPage'));
const ProjectUploadRfpPage = lazy(() => import('@features/projects/pages/ProjectUploadRfpPage'));
const ProjectUploadVendorsPage = lazy(() => import('@features/projects/pages/ProjectUploadVendorsPage'));
const ProjectCreatePage = lazy(() => import('@features/projects/pages/ProjectCreatePage'));
const Profile = lazy(() => import('@features/profile/pages/Profile'));
const Settings = lazy(() => import('@features/settings/pages/Settings'));
const ComponentsShowcase = lazy(() => import('@features/showcase/pages/ComponentsShowcase'));
const ArchitectureDocs = lazy(() => import('@features/documentation/pages/ArchitectureDocs'));
const UserManagement = lazy(() => import('@features/users/pages/UserManagement'));
const Unauthorized = lazy(() => import('@features/error/pages/Unauthorized'));
const NotFound = lazy(() => import('@features/error/pages/NotFound'));

const ConfigurationList = lazy(() => import('@features/configuration/pages/ConfigurationList'));
const EvaluationCriteriaManagement = lazy(
  () => import('@features/evaluation-criteria/pages/EvaluationCriteriaManagement')
);
const TagManagement = lazy(() => import('@features/tag/pages/TagManagement'));
const CategoryManagement = lazy(() => import('@features/category/pages/CategoryManagement'));
const SectionAnalysisPage = lazy(() => import('@features/section-analysis/pages/SectionAnalysisPage'));

/**
 * Application routes component
 */
export const AppRoutes: React.FC = () => {
  return (
    <ErrorBoundary componentName="Application Routes" showDetails={import.meta.env.DEV}>
      <Suspense fallback={<LoadingFallback fullScreen />}>
        <Routes>
          {/* Public routes */}
          <Route element={<AuthLayout />}>
            <Route path={ROUTES.LOGIN} element={wrapRoute(<Login />)} />
            <Route path={ROUTES.REGISTER} element={wrapRoute(<Register />)} />
            <Route path={ROUTES.UNAUTHORIZED} element={wrapRoute(<Unauthorized />)} />
          </Route>

          {/* Protected routes */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              <Route path={ROUTES.HOME} element={<Navigate to={ROUTES.PROJECTS} replace />} />
              <Route path={ROUTES.DASHBOARD} element={<Navigate to={ROUTES.PROJECTS} replace />} />
              <Route path={ROUTES.PROJECTS} element={wrapRoute(<Projects />)} />
              <Route path={ROUTES.PROJECT_CREATE} element={wrapRoute(<ProjectCreatePage />)} />
              <Route path={ROUTES.PROJECT_EDIT} element={wrapRoute(<ProjectEditPage />)} />
              <Route path={ROUTES.PROJECT_UPLOAD_RFP} element={wrapRoute(<ProjectUploadRfpPage />)} />
              <Route path={ROUTES.PROJECT_UPLOAD_VENDORS} element={wrapRoute(<ProjectUploadVendorsPage />)} />
              <Route path={ROUTES.USERS} element={wrapRoute(<UserManagement />)} />
              <Route path={ROUTES.PROFILE} element={wrapRoute(<Profile />)} />
              <Route path={ROUTES.SETTINGS} element={wrapRoute(<Settings />)} />
              <Route path={ROUTES.SHOWCASE} element={wrapRoute(<ComponentsShowcase />)} />
              <Route path={ROUTES.DOCS} element={wrapRoute(<ArchitectureDocs />)} />
              <Route path={ROUTES.CONFIGURATION_LIST} element={wrapRoute(<ConfigurationList />)} />
              <Route path={ROUTES.CATEGORY_MANAGEMENT} element={wrapRoute(<CategoryManagement />)} />
              <Route
                path={ROUTES.EVALUATION_CRITERIA_MANAGEMENT}
                element={wrapRoute(<EvaluationCriteriaManagement />)}
              />
              <Route path={ROUTES.TAG_MANAGEMENT} element={wrapRoute(<TagManagement />)} />
              <Route path={ROUTES.SECTION_ANALYSIS} element={wrapRoute(<SectionAnalysisPage />)} />
              <Route path={ROUTES.SECTION_ANALYSIS_ALL} element={wrapRoute(<SectionAnalysisPage />)} />
            </Route>
          </Route>

          {/* 404 route */}
          <Route path={ROUTES.NOT_FOUND} element={wrapRoute(<NotFound />)} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
};

export default AppRoutes;
